import * as React from "react";
import { format } from "date-fns";
import { Eye, Heart, MessageCircle, Share2 } from "lucide-react";
import { Badge, Button, Card } from "@/components/ui";
import { StatItem } from "@/components/PostDialogs/PostDetailsDialog/components/StatItem";
import { formatRelativeTime, getSourceIcon } from "@/components/reviews/utils";
import { socialNetworkNames, SocialNetworksId } from "@/interfaces";
import { PostNetworkStatus, ReviewPostNetwork, ReviewPostSummary } from "@/services/bedrock/dtos/ReviewPost";
import { MiniPager } from "./MiniPager";

export interface SharedOnSocialSectionProps {
  reviewId: number;
  posts: ReviewPostSummary[];
  mode?: "player" | "coach";
  playerName?: string;
  onShareAgain: () => void;
  onOpenPost: (postId: number, socialNetworkId: SocialNetworksId | null) => void;
  /** False once the review can't be posted anymore (consent withdrawn, deactivated). Default true. */
  canShareAgain?: boolean;
}

/** One line for where a post stands: scheduled, posting, posted, failed, or removed. */
export function statusLine(post: ReviewPostSummary): string {
  switch (post.status) {
    case "scheduled":
      // formatRelativeTime only counts backwards, so a future date is written out.
      return `Scheduled for ${format(new Date(post.scheduledAt ?? post.createdAt), "MMM d, h:mm a")}`;
    case "posting":
      return "Posting…";
    case "posted":
      return `Posted ${formatRelativeTime(post.postedAt ?? post.createdAt)}`;
    case "failed":
      return "Couldn't post. Open to retry.";
    case "deleted":
      return "Removed from social";
  }
}

const NETWORK_STATUS_LABELS: Partial<Record<PostNetworkStatus, string>> = {
  POSTED: "Posted",
  IN_TRANSIT: "Posting…",
  FAILED: "Couldn't post",
  SKIPPED_NO_CONNECTION: "Not connected",
};

/** The network statuses each post-level rollup already says, so their chip would repeat it. */
const ROLLUP_NETWORK_STATUSES: Record<ReviewPostSummary["status"], PostNetworkStatus[]> = {
  scheduled: ["PENDING"],
  posting: ["PENDING", "IN_TRANSIT"],
  posted: ["POSTED"],
  failed: ["FAILED", "FAILURE_ACKED"],
  deleted: ["DELETED"],
};

const NetworkRow: React.FC<{ network: ReviewPostNetwork; rollup: ReviewPostSummary["status"] }> = ({
  network,
  rollup,
}) => {
  const name = socialNetworkNames[network.code as SocialNetworksId] ?? network.code;
  const chip = ROLLUP_NETWORK_STATUSES[rollup].includes(network.status)
    ? undefined
    : NETWORK_STATUS_LABELS[network.status];
  const stats = network.stats;

  return (
    <div className="flex flex-col gap-2" data-testid={`shared-network-${network.code}`}>
      <div className="flex flex-wrap items-center gap-2">
        {getSourceIcon(network.code)}
        <span className="text-sm font-semibold text-slate-700">{name}</span>
        {chip && (
          <Badge variant="outline" size="small">
            {chip}
          </Badge>
        )}
        {network.externalPostUrl && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            nativeButton={false}
            render={<a href={network.externalPostUrl} target="_blank" rel="noopener noreferrer" />}
          >
            View on {name}
          </Button>
        )}
      </div>
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatItem
            icon={<Eye className="size-4" />}
            label="Impressions"
            value={stats.impressionCount ?? stats.reachCount}
          />
          <StatItem
            icon={<Heart className="size-4" />}
            label="Reactions"
            value={stats.totalReactions ?? stats.likeCount}
          />
          <StatItem icon={<MessageCircle className="size-4" />} label="Comments" value={stats.commentCount} />
          <StatItem icon={<Share2 className="size-4" />} label="Shares" value={stats.shareCount} />
        </div>
      )}
    </div>
  );
};

export const SharedOnSocialSection: React.FC<SharedOnSocialSectionProps> = ({
  posts,
  mode = "player",
  canShareAgain = true,
  onShareAgain,
  onOpenPost,
}) => {
  const [index, setIndex] = React.useState(0);
  const current = posts[Math.min(index, posts.length - 1)];
  if (!current) return null;

  const openCurrent = () =>
    onOpenPost(current.postId, (current.primarySocialNetworkId as SocialNetworksId | null) ?? null);
  const behind = current.imageUrls.filter(url => url !== current.thumbnailUrl).slice(0, 2);

  return (
    <Card className="border border-slate-200 rounded-2xl p-5 md:p-6 gap-4" data-testid="shared-on-social">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Shared on social</span>
        {posts.length > 1 && <MiniPager index={index} total={posts.length} onChange={setIndex} />}
      </div>

      <div className="grid md:grid-cols-[120px_1fr] gap-4">
        <button
          type="button"
          onClick={openCurrent}
          className="relative w-[120px] cursor-pointer"
          aria-label="Open post"
        >
          {behind.map((url, i) => (
            <img
              key={url}
              src={url}
              alt=""
              className="absolute inset-0 aspect-square w-full rounded-xl object-cover border border-white shadow-sm"
              style={{ transform: `translate(${(i + 1) * 6}px, ${(i + 1) * -6}px)`, zIndex: -i - 1 }}
            />
          ))}
          {current.thumbnailUrl ? (
            <img
              src={current.thumbnailUrl}
              alt=""
              className="relative aspect-square w-full rounded-xl object-cover border border-slate-200"
            />
          ) : (
            <div className="relative aspect-square w-full rounded-xl bg-slate-100" />
          )}
        </button>

        <div className="flex flex-col gap-3 min-w-0">
          <button
            type="button"
            onClick={openCurrent}
            className="text-left text-sm font-semibold text-slate-800 hover:underline cursor-pointer"
          >
            {statusLine(current)}
          </button>
          {mode === "coach" && current.createdBy && (
            <span className="text-xs text-slate-500">Posted by {current.createdBy.name}</span>
          )}
          {current.networks.map(network => (
            <NetworkRow key={network.code} network={network} rollup={current.status} />
          ))}
          {canShareAgain && (
            <div>
              <Button variant="link" size="sm" className="text-slate-500 px-0" onClick={onShareAgain}>
                Share again
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
